import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, MapPin } from "lucide-react";

// Deterministic hash from string -> number
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

const GRADIENT_PALETTES = [
  "from-slate-700 to-slate-900",
  "from-zinc-700 to-zinc-900",
  "from-stone-700 to-stone-800",
  "from-neutral-700 to-neutral-900",
  "from-gray-700 to-gray-900",
  "from-slate-800 to-zinc-700",
  "from-stone-800 to-neutral-700",
  "from-zinc-800 to-stone-700",
];

function getGradientForCity(cityKey: string): string {
  const idx = hashString(cityKey) % GRADIENT_PALETTES.length;
  return GRADIENT_PALETTES[idx];
}

interface CityCardProps {
  city: string;
  count: number;
  slug: string;
  heroImageUrl?: string | null;
  heroImageAlt?: string | null;
}

function CityCard({ city, count, slug, heroImageUrl, heroImageAlt }: CityCardProps) {
  const hasImage = !!heroImageUrl;

  return (
    <Link
      to={`/salas/${slug}/`}
      className="group relative overflow-hidden rounded-lg aspect-[4/3] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      aria-label={`Ver salas de conciertos en ${city}`}
    >
      {hasImage ? (
        <>
          <img
            src={heroImageUrl}
            alt={heroImageAlt ?? `Salas de conciertos en ${city}`}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
        </>
      ) : (
        <div
          className={`h-full w-full bg-gradient-to-br ${getGradientForCity(slug || city)} transition-all duration-300 group-hover:brightness-110`}
        />
      )}

      <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
        {!hasImage && (
          <MapPin className="mb-1.5 h-4 w-4 opacity-60" />
        )}
        <h3 className="font-display text-lg font-semibold">{city}</h3>
        <div className="flex items-center justify-between">
          <span className="text-sm opacity-90">{count} salas disponibles</span>
          <ArrowRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      </div>
    </Link>
  );
}

export function CityExplorer() {
  const { data: dbCities } = useQuery({
    queryKey: ["cities-db"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("cities")
        .select("name, slug, hero_image_url, hero_image_alt")
        .eq("is_active", true);
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 60 * 10,
  });

  const { data } = useQuery({
    queryKey: ["city-explorer"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("venues")
        .select("city")
        .eq("status", "active");
      if (error) throw error;
      const counts: Record<string, number> = {};
      data.forEach((v) => {
        counts[v.city] = (counts[v.city] || 0) + 1;
      });
      return Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([city, count]) => ({ city, count }));
    },
    staleTime: 1000 * 60 * 10,
  });

  if (!data || data.length === 0) return null;

  const getCityData = (cityName: string) => {
    const match = dbCities?.find((c) => c.name === cityName);
    return {
      slug: match?.slug ?? encodeURIComponent(cityName.toLowerCase()),
      heroImageUrl: match?.hero_image_url ?? null,
      heroImageAlt: match?.hero_image_alt ?? null,
    };
  };

  const topCities = data.slice(0, 4);
  const moreCities = data.slice(4, 24);

  return (
    <section className="container py-12">
      <div className="mb-8">
        <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
          Explora salas por ciudad
        </h2>
        <p className="mt-2 text-muted-foreground">
          Descubre las mejores salas de conciertos en las principales ciudades de España
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {topCities.map(({ city, count }) => {
          const { slug, heroImageUrl, heroImageAlt } = getCityData(city);
          return (
            <CityCard
              key={city}
              city={city}
              count={count}
              slug={slug}
              heroImageUrl={heroImageUrl}
              heroImageAlt={heroImageAlt}
            />
          );
        })}
      </div>

      {moreCities.length > 0 && (
        <div className="mt-8">
          <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
            Más ciudades
          </h3>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 md:grid-cols-4">
            {moreCities.map(({ city, count }) => {
              const { slug } = getCityData(city);
              return (
                <Link
                  key={city}
                  to={`/salas/${slug}/`}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {city} ({count})
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
